import { useApi } from '@/config/apiFetcher'
import { useUserStore } from '@/store/userStore'
import { storeToRefs } from 'pinia'
import { fetchAuthSession } from 'aws-amplify/auth'
import { signOut, confirmSignIn } from 'aws-amplify/auth'
import { useRouter } from 'vue-router'
import { mqttStore } from '@/store/mqtt'
import { streamStore } from '@/store/stream'
import { useProjectsStore } from '@/store/projectStore'
import { adminAccess, ADMIN_ORGANISATION_ID } from '@/constants/appData'
import { viewsStore } from '@/store/views'
import useUserSettings from '@/composables/useUserSettings'
import useApp from './useApp'
import { setSentryUser, clearSentryUser, captureError, trackUserAction } from '@/observability/sentry'

import type { Device } from '@/types/devices.types'
import log from '@/utils/useLogger'
import { computed, getCurrentInstance, ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'

const isLoggingOut = ref(false)

/** Wrong TOTP/MFA code — user can retry; must not sign out or redirect to login. */
function isMfaCodeMismatchError(err: unknown): boolean {
  if (err instanceof Error && err.name === 'CodeMismatchException') return true
  if (typeof err === 'object' && err !== null && '__type' in err) {
    const t = String((err as { __type?: string }).__type)
    return t === 'CodeMismatchException' || t.includes('CodeMismatch')
  }
  return false
}

export const useUser = () => {
  const userStore = useUserStore()
  const queryClient = useQueryClient()
  const instance = getCurrentInstance()
  const router = instance ? useRouter() : null
  const api = useApi()
  const { disconnectShadows, disconnectSession } = mqttStore()
  const { setInfo } = viewsStore()
  const { fetchFromCloud } = useUserSettings()
  const { selectedRC, selectedDock, organizationDevices } = useApp()
  const {
    organisation,
    allDeviceGroups,
    organisationId,
    token,
    allUsers,
    userDetails,
    userId,
    isLoggedIn,
    userSub,
  } = storeToRefs(userStore)
  const deviceManagementUrl = import.meta.env.VITE_APP_HTTP_ENPOINT

  // to be called after signIn is confirmed
  const setOrgId = async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true })
      const idToken = session.tokens?.idToken
      if (!idToken) {
        log.error('No ID token found in session')
        return false
      }

      // Use type assertions to handle the payload properties
      const org = idToken.payload['custom:organisation'] as string
      const orgId = idToken.payload['custom:attribute1'] as string
      const username = idToken.payload['cognito:username'] as string
      const email = idToken.payload['email'] as string

      organisation.value = org as any
      organisationId.value = orgId as any
      token.value = idToken.toString() as any
      userDetails.value = idToken.payload
      userSub.value = session.userSub
      isLoggedIn.value = true

      // Set Sentry user context for error tracking
      setSentryUser({
        id: session.userSub || '',
        username: username || '',
        email: email,
        organisation: org,
      })

      return true
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAuthorizedException') {
        await triggerLogout()
        return false
      }
      return false
    }
  }
  const confirmUserSignIn = async (code: string) => {
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: code })
      if (isSignedIn) {
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken
        if (!idToken) {
          log.error('No ID token found in session')
          return false
        }

        const org = idToken.payload['custom:organisation'] as string
        const orgId = idToken.payload['custom:attribute1'] as string
        const username = idToken.payload['cognito:username'] as string
        const email = idToken.payload['email'] as string

        organisation.value = org as any
        organisationId.value = orgId as any
        token.value = idToken.toString() as any
        userDetails.value = idToken.payload
        userSub.value = session.userSub
        isLoggedIn.value = true

        // Set Sentry user context for error tracking
        setSentryUser({
          id: session.userSub || '',
          username: username || '',
          email: email,
          organisation: org,
        })

        // Sync settings from cloud after login (cross-device sync)
        fetchFromCloud().catch((err) => log.warn('Cloud settings sync failed:', err))

        return true
      }
      return false
    } catch (err) {
      if (isMfaCodeMismatchError(err)) {
        throw err
      }
      captureError(err, { context: 'confirmUserSignIn' })
      await logOut()
      return false
    }
  }
  const logOut = async () => {
    if (isLoggingOut.value) return
    try {
      isLoggingOut.value = true
      // Tear down active streams while user credentials are still available,
      // so the STOP_STREAM_* MQTT commands can be sent to the device.
      const streams = streamStore()
      await Promise.allSettled([
        streams.leaveAndResetStreamForDevice('uav'),
        streams.leaveAndResetStreamForDevice('dock'),
        streams.leaveAndResetStreamForDevice('slinktech'),
      ])
      // FLY3D-1029: publish retained `offline` and close the session client
      // before signing out, so colleagues see a clean departure immediately
      // rather than waiting out the 60s keepalive for the will message.
      await disconnectSession()
      disconnectShadows(selectedRC.value, selectedDock.value)
      invalidate.users()
      invalidate.deviceGroups()
      invalidate.deviceGroupIds()
      invalidate.deviceIds()
      clearSentryUser()
      await signOut()
      await router?.push({ name: 'Login' })
      userStore.$reset()
      // FLY3D-938: the next login must pass through the project gate again.
      useProjectsStore().clearActiveProject()
    } catch (error) {
      captureError(error, { context: 'logOut' })
    } finally {
      isLoggingOut.value = false
    }
  }
  const triggerLogout = async () => {
    setInfo(
      'Session Terminated',
      'You have been logged out because your account is now active on another device. Only one active session is allowed per user.',
      3000,
    )
    await logOut()
  }
  const checkIfLoggedIn = async () => {
    try {
      if (!isLoggedIn.value || !token.value) return false
      return await setOrgId()
    } catch (err) {
      await logOut()
      return false
    }
  }
  const fetchUsers = () => {
    return useQuery({
      queryKey: computed(() => ['users', organisationId.value]),
      queryFn: async () => {
        const res = await api.get(
          `${import.meta.env.VITE_APP_USER_MANAGEMENT}/users?organisationID=${organisationId.value}`,
        )
        const list = res.data.userList
        allUsers.value = list
        return list
      },
      enabled: computed(() => !!organisationId.value && !!isLoggedIn.value),
      staleTime: 1000 * 60 * 10,
      gcTime: 1000 * 60 * 20,
      retry: 2,
      retryDelay: 1000,
    })
  }
  /**
   * Device list (FLY3D-938 project scoping).
   *
   * scope 'project' (default): server-filtered to the active project and the
   * ONLY writer of `organizationDevices`, so `groupedDevices` and every
   * picker/map view stay project-scoped. Waits for a project selection.
   *
   * scope 'org': the whole fleet, for admin/setup/sharing surfaces that must
   * see every org device — never writes the store (it would leak org-wide
   * devices into the scoped views).
   */
  const fetchDevices = (scope: 'project' | 'org' = 'project') => {
    const scoped = scope === 'project'
    const { activeProjectId } = storeToRefs(useProjectsStore())
    return useQuery({
      queryKey: computed(() => [
        'devices',
        organisationId.value,
        scoped ? activeProjectId.value : 'all',
      ]),
      queryFn: async () => {
        const devices = await api.get(deviceManagementUrl + 'device-management/devices', {
          params: {
            organisationID: organisationId.value,
            ...(scoped && activeProjectId.value ? { projectID: activeProjectId.value } : {}),
          },
        })
        if (scoped) organizationDevices.value = devices.data.deviceIDList
        return devices.data.deviceIDList
      },
      enabled: computed(
        () =>
          !!organisationId.value &&
          isLoggedIn.value &&
          (!scoped || !!activeProjectId.value),
      ),
      staleTime: 1000 * 60 * 10,
      gcTime: 1000 * 60 * 20,
      retry: 2,
      retryDelay: 1000,
    })
  }
  const updateDeviceName = async (deviceID: string, deviceName: string) => {
    try {
      const res = await api.post(deviceManagementUrl + 'device-management/update-device', {
        deviceID,
        deviceName,
        organisationID: organisationId.value,
      })
      invalidate.devices()
      return res.data
    } catch (err) {
      captureError(err, { context: 'updateDeviceName', deviceID, deviceName })
      return false
    }
  }
  const invalidate = {
    users: () => {
      queryClient.invalidateQueries({ queryKey: ['users', organisationId.value] })
    },
    deviceGroupIds: () => {
      queryClient.invalidateQueries({
        queryKey: ['deviceGroupIds', organisationId.value, userId.value],
      })
    },
    deviceGroups: () => {
      // Prefix match: refreshes both the project-scoped and 'all' variants.
      queryClient.invalidateQueries({ queryKey: ['deviceGroups', organisationId.value], exact: false })
    },
    deviceIds: () => {
      // Prefix match: refreshes both the project-scoped and 'all' variants.
      queryClient.invalidateQueries({ queryKey: ['devices', organisationId.value], exact: false })
    },
    devices: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', organisationId.value], exact: false })
    },
  }
  /**
   * Device groups (FLY3D-938 project scoping).
   *
   * Groups have no table of their own — the backend derives them by scanning
   * the devices table, and devices carry `projectID`. So a group belongs to a
   * project exactly when at least one of its devices does.
   *
   * scope 'project' (default): server-filtered to the active project and the
   * ONLY writer of `allDeviceGroups`. Waits for a project selection.
   *
   * scope 'org': the whole org's groups, for admin surfaces that must see
   * every group — never writes the store (it would leak out-of-project groups
   * into the scoped views). Read the returned query's `data` instead.
   */
  const getAllGroups = (scope: 'project' | 'org' = 'project') => {
    const scoped = scope === 'project'
    const { activeProjectId } = storeToRefs(useProjectsStore())
    return useQuery({
      queryKey: computed(() => [
        'deviceGroups',
        organisationId.value,
        scoped ? activeProjectId.value : 'all',
      ]),
      queryFn: async () => {
        const params = new URLSearchParams({ organisationID: organisationId.value ?? '' })
        if (scoped && activeProjectId.value) params.append('projectID', activeProjectId.value)
        const res = await api.get(
          `${deviceManagementUrl}/device-management/get-all-groups?${params.toString()}`,
        )
        const groups = res.data.groups ?? []
        if (scoped) allDeviceGroups.value = groups
        return groups
      },
      enabled: computed(
        () => !!organisationId.value && !!isLoggedIn.value && (!scoped || !!activeProjectId.value),
      ),
      staleTime: 1000 * 60 * 10,
      gcTime: 1000 * 60 * 20,
      retry: 2,
      retryDelay: 1000,
    })
  }

  /**
   * Device groups that actually hold a device in the active project.
   *
   * Defence in depth on top of the server-side `projectID` filter above: it is
   * correct even against a device-management deployment that predates IFB-117
   * (where the new param is ignored), because `organizationDevices` is only
   * ever written by the project-scoped `fetchDevices()`. Group objects are
   * passed through whole, so the dock lat/lon that `get-all-groups` derives
   * from `dockConfig` survives the filter.
   */
  const projectDeviceGroups = computed(() => {
    const groups = Array.isArray(allDeviceGroups.value) ? allDeviceGroups.value : []
    if (!groups.length) return []
    const inScope = new Set(
      organizationDevices.value.map((d: Device) => d.deviceGroupID).filter(Boolean),
    )
    return groups.filter((g: any) => g?.deviceGroupID && inScope.has(g.deviceGroupID))
  })
  return {
    setOrgId,
    getAllGroups,
    fetchUsers,
    logOut,
    confirmUserSignIn,
    triggerLogout,
    checkIfLoggedIn,
    allDeviceGroups,
    projectDeviceGroups,
    invalidate,
    fetchDevices,
    updateDeviceName,
  }
}
export const isAdminUser = () => {
  const userStore = useUserStore()
  const { organisationId, userId } = storeToRefs(userStore)

  if (!userId.value || !organisationId.value) return false

  // Must be from IdroneImages organization AND be in admin list
  return (
    organisationId.value === ADMIN_ORGANISATION_ID &&
    adminAccess.includes(userId.value)
  )
}
